import { isDefined } from "@clipboard-health/util-ts";
import {
  useWorkplaceRules,
  type WorkplaceRulesResponse,
} from "@src/appV2/Facilities/api/useWorkplaceRules";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useGetWorkplaceProfile } from "@src/appV2/redesign/Workplace/useGetWorkplaceProfile";
import { useIsQuizWorkersOnWorkplaceRulesFeatureEnabled } from "@src/appV2/WorkplaceQuiz/utils/useIsQuizWorkersOnWorkplaceRulesEnabled";

interface UseShowWorkplaceRulesReminderProps {
  workplaceId?: string;
}

export function useShowWorkplaceRulesReminder({
  workplaceId,
}: UseShowWorkplaceRulesReminderProps): {
  shouldShowWorkplaceRulesReminder: boolean;
  workplaceRules: WorkplaceRulesResponse | undefined;
  isLoading: boolean;
} {
  const { isEnabled: isClockInRulesReminderEnabled } = useCbhFlag(
    CbhFeatureFlag.CLOCK_IN_RULES_REMINDER_ENABLED,
    {
      defaultValue: {
        isEnabled: false,
      },
    }
  );

  // Fetch rules and workplace profile to check if the quiz is enabled for the facility
  const { data: workplaceRules, isLoading: isRulesLoading } = useWorkplaceRules(workplaceId ?? "", {
    enabled: isDefined(workplaceId),
  });
  const { data: workplaceProfile, isLoading: isWorkplaceProfileLoading } = useGetWorkplaceProfile(
    { workplaceId: workplaceId ?? "" },
    { enabled: isDefined(workplaceId) }
  );
  const isQuizWorkersOnWorkplaceRulesFeatureEnabled =
    useIsQuizWorkersOnWorkplaceRulesFeatureEnabled({
      workplace: {
        quizWorkersOnRules: workplaceProfile?.quizWorkersOnRules ?? undefined,
      },
    });

  const shouldShowWorkplaceRulesReminder =
    // Feature flag must be enabled
    isClockInRulesReminderEnabled &&
    // Workplace has at least some rules linked to a quiz question
    isDefined(workplaceRules) &&
    workplaceRules.some((rule) => isDefined(rule.quizQuestionId)) &&
    // Quiz is enabled for the workplace
    isQuizWorkersOnWorkplaceRulesFeatureEnabled;

  return {
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isLoading: isRulesLoading || isWorkplaceProfileLoading,
  };
}
